
import React, { useState } from 'react';
import { cn } from "@/lib/utils";
import { ActionCardProps } from '@/types/action-card';
import { motion, AnimatePresence } from 'framer-motion';
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ChevronDown, ChevronUp, Table2, ArrowUpDown } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Pagination } from '@/components/ui/pagination';

export const DataTableCard = ({ data, className }: ActionCardProps) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState<{
    key: number;
    direction: 'asc' | 'desc' | null;
  }>({ key: -1, direction: null });
  
  const itemsPerPage = 5;
  
  // Make sure we have table data
  const headers = data.tableData?.headers || [];
  const allRows = data.tableData?.rows || [];
  
  // Filter rows based on search query
  const filteredRows = allRows.filter(row =>
    !searchQuery || row.some(cell => 
      String(cell).toLowerCase().includes(searchQuery.toLowerCase())
    )
  );
  
  // Sort rows if needed
  const sortedRows = [...filteredRows];
  if (sortConfig.direction && sortConfig.key >= 0) {
    sortedRows.sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }
  
  // Paginate rows
  const totalPages = Math.ceil(sortedRows.length / itemsPerPage);
  const paginatedRows = sortedRows.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  
  const handleSort = (columnIndex: number) => {
    let direction: 'asc' | 'desc' | null = 'asc';
    
    if (sortConfig.key === columnIndex) {
      if (sortConfig.direction === 'asc') {
        direction = 'desc';
      } else if (sortConfig.direction === 'desc') {
        direction = null;
      }
    }
    
    setSortConfig({
      key: columnIndex,
      direction,
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ 
        type: "spring",
        stiffness: 500,
        damping: 30
      }}
      className={cn(
        "relative",
        "shadow-lg hover:shadow-xl transition-all duration-300",
        className
      )}
    >
      <Card className={cn(
        "backdrop-blur-sm bg-white/10 dark:bg-gray-900/20",
        "border border-white/20 dark:border-gray-800/30",
        "bg-gradient-to-br from-primary/10 to-primary/5",
        "p-4"
      )}>
        {/* Header */}
        <div className="flex items-center justify-between pb-3 border-b border-white/10 dark:border-gray-800/50">
          <div className="flex items-center gap-3">
            <motion.div 
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="relative flex h-8 w-8 items-center justify-center rounded-lg bg-white/20 dark:bg-gray-800/30"
            >
              <Table2 className="h-4 w-4 text-primary" />
            </motion.div>
            <div className="space-y-0.5">
              <h3 className="text-lg font-semibold tracking-tight">{data.title}</h3>
              <p className="text-sm text-muted-foreground">{data.summary}</p>
            </div>
          </div>
          
          {data.isCollapsible && (
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-white/10 dark:hover:bg-gray-800/50 transition-colors"
              aria-label={isCollapsed ? "Expand card" : "Collapse card"}
            >
              {isCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
            </button>
          )}
        </div>

        {/* Content */}
        <AnimatePresence>
          {!isCollapsed && headers.length > 0 && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              {/* Search input */}
              {allRows.length > 5 && (
                <div className="pt-4 pb-2">
                  <Input
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setCurrentPage(1); // Reset to first page when searching
                    }}
                    placeholder="Search table..."
                    className="bg-white/10 dark:bg-gray-800/30 border-white/10 dark:border-gray-800/30"
                  />
                </div>
              )}

              {/* Table */}
              <div className="pt-2 overflow-x-auto">
                <Table className="border-collapse">
                  <TableHeader>
                    <TableRow className="border-b border-white/10 dark:border-gray-800/50">
                      {headers.map((header, index) => (
                        <TableHead 
                          key={index}
                          className="text-foreground"
                        >
                          <Button
                            variant="ghost"
                            className="p-0 h-auto font-medium flex items-center hover:bg-transparent hover:text-primary"
                            onClick={() => handleSort(index)}
                          >
                            {header}
                            {sortConfig.key === index && (
                              <span className="ml-1">
                                {sortConfig.direction === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                            {sortConfig.key !== index && (
                              <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />
                            )}
                          </Button>
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedRows.length > 0 ? (
                      paginatedRows.map((row, index) => (
                        <TableRow 
                          key={index}
                          className="border-b border-white/5 dark:border-gray-800/30 hover:bg-white/5 dark:hover:bg-gray-800/20"
                        >
                          {row.map((cell, cellIndex) => (
                            <TableCell key={cellIndex} className="py-2">
                              {String(cell)}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={headers.length} className="py-6 text-center text-muted-foreground">
                          {searchQuery ? "No results match your search" : "No data available"}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="py-4 flex justify-center">
                  <Pagination>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="bg-white/10 dark:bg-gray-800/30 border-white/10 dark:border-gray-800/30"
                    >
                      Previous
                    </Button>
                    <div className="mx-2 flex items-center text-sm">
                      Page {currentPage} of {totalPages}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="bg-white/10 dark:bg-gray-800/30 border-white/10 dark:border-gray-800/30"
                    >
                      Next
                    </Button>
                  </Pagination>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Timestamp */}
        <div className="pt-2 text-[10px] text-muted-foreground">
          {data.timestamp}
        </div>
      </Card>
    </motion.div>
  );
};
